/**
 * LeadDetailRepPage — web `/leads/:id`, the rep-facing lead overview.
 *
 * L192 (2026-07-16). Clicking any lead notification in the web tray landed on
 * "Page not found". The notification's href is built by the backend
 * (`modules/notifications/notification-links.ts:16` → `/leads/${leadId}`) and
 * `components/layout/TopBar.tsx:150` navigates it verbatim — but the web app
 * had no `/leads/:id` route. Mobile works because it routes the notification's
 * leadId programmatically and never navigates the href, so the shared link
 * string was correct for mobile and dead for web. This page is the missing
 * target; the link stays single-source.
 *
 * ── Why this is NOT `lead-manager/LeadDetailPage` ────────────────────────────
 *
 * Reusing that page here would have swapped a loud 404 for a quiet empty one:
 *
 *  1. It fetches `/leads/${id}`. Because `index.ts:912` mounts `dealsRoutes` on
 *     `/api/v1/leads`, that path resolves to `dealsRoutes.get('/:id')` — a
 *     **Deal** lookup, gated by `requireOwnershipOr('deal')`. Notification ids
 *     are **LeadImport** ids (`imp1bf186…`), which have no Deal row, so it
 *     404s. `safeGet` swallows the 404 and renders an empty shell.
 *  2. Three of its six endpoints (`/verification-trail`,
 *     `/reassignment-history`, `/team-activities`) do not exist in the backend
 *     at all.
 *  3. Its affordances are lead-manager ones — reassign, reject, verification
 *     trail, assigned-team oversight. Wrong surface for a rep opening their
 *     own lead alert.
 *
 * This page instead reads `GET /lead-integrations/lead-imports/:id`, which is
 * `authenticate`-only (so **rep-openable**, per the locked constraint), is
 * LeadImport-first with a Deal fallback, and is the same feed the mobile lead
 * detail already uses. Single source, no new backend.
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Phone,
  MessageSquare,
  UserSearch,
} from 'lucide-react';
import { safeGet } from '@/pages/dashboard/roles/_dashboardShared';
import { telHref, waHref, formatPhone } from '@/utils/formatters';
import {
  Avatar,
  EmptyState,
  FieldLabel,
  PriorityChip,
  StatusChip,
  relativeTime,
} from '@/pages/lead-manager/_lmShared';

/**
 * The subset of `GET /lead-integrations/lead-imports/:id` this page renders.
 * Every field is optional: the endpoint answers with either a LeadImport row
 * or its Deal-fallback shape (for historic deals with no LeadImport linkage),
 * and the two do not carry the same keys.
 */
interface RepLead {
  id?: string;
  name?: string | null;
  displayName?: string | null;
  company?: string | null;
  companyName?: string | null;
  phone?: string | null;
  email?: string | null;
  product?: string | null;
  quantity?: string | null;
  location?: string | null;
  message?: string | null;
  notes?: string | null;
  requirement?: string | null;
  sourceName?: string | null;
  source?: string | null;
  score?: number | null;
  /** CANONICAL tier: 'HOT' | 'WARM' | 'COLD', or null while unscored. */
  interestLevel?: string | null;
  verificationStatus?: string | null;
  assignedToName?: string | null;
  lastCalledAt?: string | null;
  createdAt?: string | null;
  nextFollowUp?: string | null;
}

function displayName(l: RepLead): string {
  return (l.displayName || l.name || l.company || l.companyName || '').trim() || 'Unknown lead';
}

function companyOf(l: RepLead): string | null {
  const c = (l.company || l.companyName || '').trim();
  const n = (l.displayName || l.name || '').trim();
  // Don't echo the company back as a subtitle when it IS the title.
  return c && c !== n ? c : null;
}

/**
 * `interestLevel` → the tier `PriorityChip` renders.
 *
 * ⚠️ Deliberately NOT `_lmShared`'s `priorityFrom()`. That helper reads
 * `lead.priority`, which on a LeadImport is the `low|medium|high|urgent`
 * workflow priority — NOT a temperature — and falls back to 'warm' for every
 * unrecognised value. Feeding it a LeadImport would silently chip every lead
 * "Warm". The tier lives on `interestLevel` ('HOT'|'WARM'|'COLD'), null while
 * the lead is unscored.
 */
function tierOf(l: RepLead): 'hot' | 'warm' | 'cold' | null {
  const t = (l.interestLevel ?? '').toLowerCase();
  return t === 'hot' || t === 'warm' || t === 'cold' ? t : null;
}

/** Value row. Renders an em-dash rather than collapsing, so the grid stays readable. */
function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <div style={{ fontSize: 13, color: 'var(--ink-1)' }}>
        {value && value.trim() ? value : '—'}
      </div>
    </div>
  );
}

export function LeadDetailRepPage() {
  const { id } = useParams<{ id: string }>();
  const [lead, setLead] = useState<RepLead | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let live = true;
    setLoading(true);
    safeGet<RepLead>(`/lead-integrations/lead-imports/${id}`).then((r) => {
      if (!live) return;
      setLead(r.data);
      setError(r.error);
      setLoading(false);
    });
    return () => {
      live = false;
    };
  }, [id]);

  const phone = lead?.phone ?? '';
  const tier = lead ? tierOf(lead) : null;

  return (
    <div
      className="aura-root"
      style={{ padding: 24, background: 'var(--ivory-0)', minHeight: '100%' }}
    >
      <div style={{ marginBottom: 18, display: 'flex', alignItems: 'center', gap: 12 }}>
        <Link
          to="/leads"
          style={{
            color: 'var(--ink-3)',
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 12,
          }}
        >
          <ArrowLeft size={12} /> Back to leads
        </Link>
      </div>

      {loading ? (
        <div
          style={{
            background: 'var(--ivory-1)',
            border: '1px solid var(--line)',
            borderRadius: 10,
            padding: 28,
            fontSize: 13,
            color: 'var(--ink-3)',
            textAlign: 'center',
          }}
        >
          Loading…
        </div>
      ) : !lead ? (
        <div
          style={{
            background: 'var(--ivory-1)',
            border: '1px solid var(--line)',
            borderRadius: 10,
            padding: 36,
          }}
        >
          <EmptyState
            icon={<UserSearch size={28} style={{ color: 'var(--ink-3)' }} />}
            title={error ? 'Could not open this lead' : 'Lead not found'}
            sub={
              error
                ? `The lead could not be loaded (${error}). It may have been reassigned or removed.`
                : 'This lead no longer exists — it may have been merged, rejected, or removed.'
            }
          />
        </div>
      ) : (
        <>
          {/* ── Identity + the two actions a rep opens a notification to take ── */}
          <div
            style={{
              background: 'var(--ivory-1)',
              border: '1px solid var(--line)',
              borderRadius: 10,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                padding: 20,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 14,
                flexWrap: 'wrap',
              }}
            >
              <Avatar name={displayName(lead)} size={44} />

              <div style={{ flex: 1, minWidth: 200 }}>
                <h2
                  style={{
                    fontFamily: 'var(--ff-serif)',
                    fontSize: 22,
                    fontWeight: 500,
                    marginBottom: 2,
                    color: 'var(--ink-0)',
                  }}
                >
                  {displayName(lead)}
                </h2>
                {companyOf(lead) && (
                  <div style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 4 }}>
                    {companyOf(lead)}
                  </div>
                )}
                <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                  {lead.sourceName || lead.source || 'Unknown source'}
                  {lead.createdAt ? ` · ${relativeTime(lead.createdAt)} ago` : ''}
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  gap: 6,
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                {/* Tier is absent until Avika scores the lead — say so rather
                    than defaulting to a temperature the rep would act on. */}
                {tier ? (
                  <PriorityChip p={tier} />
                ) : (
                  <span className="chip">Unscored</span>
                )}
                <StatusChip status={lead.verificationStatus} />
              </div>
            </div>

            {/* Call / WhatsApp — anchors, not JS handlers, so they behave like
                links (middle-click, long-press) and need no dial plumbing. */}
            {phone && (
              <div
                style={{
                  padding: '0 20px 20px',
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <a
                  href={telHref(phone)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 13,
                    fontWeight: 500,
                    padding: '8px 14px',
                    borderRadius: 8,
                    textDecoration: 'none',
                    color: 'var(--ivory-0)',
                    background: 'var(--forest)',
                  }}
                >
                  <Phone size={13} /> Call {formatPhone(phone)}
                </a>
                <a
                  href={waHref(phone)}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 13,
                    fontWeight: 500,
                    padding: '8px 14px',
                    borderRadius: 8,
                    textDecoration: 'none',
                    color: 'var(--ink-1)',
                    background: 'var(--ivory-2)',
                    border: '1px solid var(--line)',
                  }}
                >
                  <MessageSquare size={13} /> WhatsApp
                </a>
              </div>
            )}

            {/* ── What the rep needs to know before dialling ── */}
            <div
              style={{
                borderTop: '1px solid var(--line)',
                padding: 20,
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                gap: 18,
              }}
            >
              <Field label="Product" value={lead.product} />
              <Field label="Quantity" value={lead.quantity} />
              <Field label="Location" value={lead.location} />
              <Field label="Assigned to" value={lead.assignedToName} />
              <Field
                label="Score"
                value={typeof lead.score === 'number' ? String(lead.score) : null}
              />
              <Field
                label="Last called"
                value={lead.lastCalledAt ? `${relativeTime(lead.lastCalledAt)} ago` : null}
              />
              <Field
                label="Next follow-up"
                value={lead.nextFollowUp ? new Date(lead.nextFollowUp).toLocaleString() : null}
              />
              <Field label="Email" value={lead.email} />
            </div>

            {(lead.requirement || lead.message || lead.notes) && (
              <div style={{ borderTop: '1px solid var(--line)', padding: 20 }}>
                {lead.requirement && (
                  <div style={{ marginBottom: lead.message || lead.notes ? 16 : 0 }}>
                    <FieldLabel>Requirement</FieldLabel>
                    <div style={{ fontSize: 13, color: 'var(--ink-1)', whiteSpace: 'pre-wrap' }}>
                      {lead.requirement}
                    </div>
                  </div>
                )}
                {lead.message && (
                  <div style={{ marginBottom: lead.notes ? 16 : 0 }}>
                    <FieldLabel>Message</FieldLabel>
                    <div style={{ fontSize: 13, color: 'var(--ink-1)', whiteSpace: 'pre-wrap' }}>
                      {lead.message}
                    </div>
                  </div>
                )}
                {lead.notes && (
                  <div>
                    <FieldLabel>Notes</FieldLabel>
                    <div style={{ fontSize: 13, color: 'var(--ink-1)', whiteSpace: 'pre-wrap' }}>
                      {lead.notes}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* The pipeline stays the place to ACT on a lead (stage, assign,
              disposition). This page exists to land the notification and hand
              off, not to grow into a second lead workbench. */}
          <div style={{ marginTop: 14 }}>
            <Link
              to="/leads"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 13,
                color: 'var(--saffron-600)',
                textDecoration: 'none',
              }}
            >
              Open the leads pipeline <ArrowRight size={13} />
            </Link>
          </div>
        </>
      )}
    </div>
  );
}

export default LeadDetailRepPage;
